/* cf-quote-dedupe.js - Click Fencing, 2026-09-02
 *
 * Pete: "this is ridiculous the amount of doubled up quotes!! also i need to be able to
 *        delete the ones i dont need. lets get this tidied up on peoples accounts"
 *
 * ---------------------------------------------------------------------------------------
 * WHAT HE IS LOOKING AT. /pages/quote-review renders one pill per entry in the four
 * localStorage arrays - flattop_quotes, blade_quotes, perfmesh_quotes, ullc_quotes. His
 * browser holds roughly ninety copies of "Blade Quote 5", forty of "PerfMesh Quote 12" and
 * so on: same name, same quoteNum, same total. They are not different plans, they are the
 * same plan stored over and over.
 *
 * WHY THIS FILE DOES NOT TRY TO NAME THE CULPRIT. Several things write those arrays - the
 * calculator's own proceedToQuotePage, cf-quote-edit's attachDesign, and cf-plans'
 * pullFromAccount, which re-adds any account row whose key does not match a local one. The
 * duplicate has been an open item since 2026-09-01 precisely because nobody has pinned it
 * on one of them, and guessing wrong would ship a fix that changes nothing.
 *
 * So this takes the other road: instead of finding every writer, it makes a duplicate
 * unable to survive being written. localStorage.setItem is wrapped, and any write to one of
 * the four quote arrays is collapsed on the way through. Whichever code path is at fault -
 * including one nobody has found yet, and including a future one - its duplicate is gone
 * before the page can render it. The arrays are also swept once on load, which is what
 * actually cleans up the browsers that are already in this state.
 *
 * WHAT COUNTS AS THE SAME PLAN. A fingerprint over what the customer would recognise:
 * calculator, quote number, name, pricing mode, grand total, and a signature of the parts
 * list and the sections. Two entries collapse ONLY when all of that matches. A genuinely
 * different plan - a different length, a gate added, a different total - differs in the
 * fingerprint and is kept. That is deliberately conservative: losing a plan somebody built
 * is far worse than leaving a duplicate on screen.
 *
 * WHICH COPY SURVIVES. The most complete one, not simply the newest: a copy carrying a
 * planRef beats one without (that is the CF-BL-7K3M9Q reference the CRM and the customer's
 * PDF are keyed on), then one carrying cfDesign (which is what "Edit sections" replays
 * from), then the newest generatedAt. Fields present on a discarded copy but missing from
 * the survivor are folded in, so collapsing can never lose the reference or the design.
 *
 * DELETING. Pete asked for it directly. Every pill gets a small x, and the action row gets
 * "Remove duplicates" whenever there are any. Delete is by calculator + quote number, which
 * is also what the pill carries, so pressing x on one of ninety identical pills removes the
 * plan rather than one of its copies. Both confirm first, and neither touches the CRM: the
 * office copy of a plan is a business record and stays put.
 *
 * SAFETY. Every entry point is wrapped. The setItem wrapper is re-entrancy guarded and
 * falls through to the original write on any error, so the worst case here is that a
 * duplicate survives - never that a plan fails to save.
 */
(function () {
  'use strict';

  if (window.__cfQuoteDedupe) return;
  window.__cfQuoteDedupe = true;

  var KEYS = ['flattop_quotes', 'blade_quotes', 'perfmesh_quotes', 'ullc_quotes'];
  var LOG = '[cf-quote-dedupe]';

  function isQuoteKey(k) {
    for (var i = 0; i < KEYS.length; i++) if (KEYS[i] === k) return true;
    return false;
  }

  /* ---------------------------------------------------------------- fingerprint */

  /* Small, stable summaries. The whole breakdown would be a fingerprint that changes when a
     price is recalculated to the same value in a different order, and the whole sections
     object carries transient fields; these take only what identifies the plan. */
  function linesSig(bd) {
    if (Object.prototype.toString.call(bd) !== '[object Array]') return '0';
    var parts = [];
    for (var i = 0; i < bd.length; i++) {
      var l = bd[i] || {};
      parts.push([l.category, l.item || l.description, l.sku || l.part_number,
                  l.quantity, l.totalPrice].join('~'));
    }
    parts.sort();
    return bd.length + ':' + parts.join('|');
  }

  function sectionsSig(s) {
    try {
      if (!s) return '0';
      var keys = Object.keys(s).sort();
      var out = [];
      for (var i = 0; i < keys.length; i++) {
        var v = s[keys[i]] || {};
        out.push([keys[i], v.length, v.panelLength, v.gapSpacing, v.hasGate, v.gateSide,
                  v.gateLength, v.gatePosition, v.hasRaked, v.retainedHeight,
                  v.color || v.colour].join('~'));
      }
      return out.join('|');
    } catch (e) { return '?'; }
  }

  /* quoteNum is DELIBERATELY not part of this, and that is the whole trick.
     cf-account.js renumbers quoteNum every time it downloads a plan from the account, so
     the ninety copies of "Blade Quote 5" in Pete's browser carry ninety DIFFERENT numbers.
     A fingerprint including the number would call them ninety different plans and collapse
     nothing at all.

     generatedAt is the plan's real identity: an ISO timestamp with milliseconds, minted
     once when the plan is saved and never rewritten by anything. When it is present it is
     used alone - two entries with the same calculator and the same millisecond ARE the
     same plan. Only when it is missing does this fall back to comparing the contents. */
  function fingerprint(q) {
    if (!q || typeof q !== 'object') return null;
    if (q.generatedAt) return String(q.calcType || '') + '@' + String(q.generatedAt);
    return [
      String(q.calcType || ''),
      String(q.quoteName || '').trim(),
      String(q.pricingType || ''),
      String(q.grandTotal == null ? '' : q.grandTotal),
      linesSig(q.breakdown),
      sectionsSig(q.sections)
    ].join('||');
  }

  /* ---------------------------------------------------------------- which copy wins */

  function score(q) {
    var s = 0;
    if (q && q.planRef) s += 4;
    if (q && q.cfDesign && q.cfDesign.sections) s += 2;
    if (q && q.quoteName) s += 1;
    return s;
  }

  function newer(a, b) {
    return (Date.parse(b && b.generatedAt) || 0) - (Date.parse(a && a.generatedAt) || 0);
  }

  /* Anything the loser carried and the winner does not, the winner keeps. Collapsing must
     never be able to drop a reference, a drawing or a saved design. */
  function absorb(winner, loser) {
    try {
      for (var k in loser) {
        if (!Object.prototype.hasOwnProperty.call(loser, k)) continue;
        var v = loser[k];
        if (v === undefined || v === null || v === '') continue;
        if (winner[k] === undefined || winner[k] === null || winner[k] === '') winner[k] = v;
      }
    } catch (e) {}
    return winner;
  }

  /* Returns a new array, or null when nothing changed - so a clean write is left completely
     alone and costs one pass. */
  function collapse(arr) {
    if (Object.prototype.toString.call(arr) !== '[object Array]') return null;
    if (arr.length < 2) return null;

    var seen = {}, order = [], dropped = 0;
    for (var i = 0; i < arr.length; i++) {
      var q = arr[i];
      var fp = fingerprint(q);
      if (fp === null) {
        seen['__raw' + i] = order.length;
        order.push({ fp: '__raw' + i, q: q });
        continue;
      }
      if (!Object.prototype.hasOwnProperty.call(seen, fp)) {
        seen[fp] = order.length;
        order.push({ fp: fp, q: q });
        continue;
      }
      dropped++;
      var slot = order[seen[fp]];
      var keepExisting = score(slot.q) > score(q) ||
                         (score(slot.q) === score(q) && newer(slot.q, q) <= 0);
      slot.q = keepExisting ? absorb(slot.q, q) : absorb(q, slot.q);
    }
    if (!dropped) return null;

    var out = [];
    for (var j = 0; j < order.length; j++) out.push(order[j].q);
    return out;
  }

  /* ---------------------------------------------------------------- storage */

  var origSet = null;
  var inWrite = false;

  function rawSet(key, value) {
    inWrite = true;
    try { (origSet || localStorage.setItem).call(localStorage, key, value); }
    finally { inWrite = false; }
  }

  function read(key) {
    try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) { return null; }
  }

  /* Sweeps what is already stored. This is the part that cleans a browser that is in the
     state Pete photographed. */
  function sweep() {
    var removed = 0;
    for (var i = 0; i < KEYS.length; i++) {
      try {
        var arr = read(KEYS[i]);
        if (!arr) continue;
        var next = collapse(arr);
        if (!next) continue;
        removed += arr.length - next.length;
        rawSet(KEYS[i], JSON.stringify(next));
      } catch (e) {}
    }
    if (removed) {
      try { console.log(LOG, 'collapsed', removed, 'duplicate plan(s)'); } catch (e) {}
    }
    return removed;
  }

  function countDuplicates() {
    var n = 0;
    for (var i = 0; i < KEYS.length; i++) {
      var arr = read(KEYS[i]);
      if (!arr) continue;
      var next = collapse(arr);
      if (next) n += arr.length - next.length;
    }
    return n;
  }

  /* The wrapper. Whichever script is re-adding the same plan, its write is collapsed here
     rather than being chased in that script - see the header. */
  function wrapSetItem() {
    try {
      if (origSet) return;
      origSet = localStorage.setItem;
      if (typeof origSet !== 'function') { origSet = null; return; }
      localStorage.setItem = function (key, value) {
        if (inWrite || !isQuoteKey(String(key))) return origSet.apply(this, arguments);
        try {
          var arr = JSON.parse(String(value));
          var next = collapse(arr);
          if (next) {
            try {
              console.log(LOG, 'blocked', arr.length - next.length, 'duplicate(s) writing', key);
            } catch (e) {}
            return origSet.call(this, key, JSON.stringify(next));
          }
        } catch (e) {}
        return origSet.apply(this, arguments);
      };
    } catch (e) { origSet = null; }
  }

  /* ------------------------------------------------- the account key mismatch

     THIS IS THE ROOT CAUSE, and it is proved by the stored data rather than inferred.
     saved_quotes on the account backend holds the SAME plan under two different keys:

         PerfMesh-15                          <- cf-plans.js  refFor()  = calcType + quoteNum
         PerfMesh:2026-09-01T11:10:04.587Z    <- cf-account.js planKey() = calcType + generatedAt

     Two scripts sync the same plans through /apps/cf-account/quotes using incompatible key
     schemes, so each one believes every plan written by the other is missing. cf-account
     downloads them all as new, RENUMBERS quoteNum on the way in and appends them; cf-plans
     then pushes those fresh local copies back up under fresh hyphen keys. Both sides grow on
     every single page load, the name never changes because only the number is rewritten, and
     you end up looking at ninety pills all called "Blade Quote 5".

     FIXED FROM OUTSIDE, ON PURPOSE. The obvious repair is to edit refFor() or planKey(), but
     both live inside 10-34KB closures that would have to be re-uploaded whole and character
     perfect, and a byte-size check cannot catch an equal-length transcription slip in the
     file that owns everybody's saved plans. So the two schemes are reconciled where they
     meet instead: every quoteKey crossing the account proxy - in either direction - is
     rewritten to the generatedAt form. Both scripts then read and write one scheme, both
     recognise what is already there, and neither re-downloads or re-uploads.

     Backing this out is deleting this block. Nothing else in the theme knows it is here. */

  var ACCOUNT_RE = /\/apps\/cf-account\/quotes/i;

  function canonicalKey(row) {
    try {
      var q = row && row.quote;
      var calc = (q && q.calcType) || (row && row.calcType);
      var stamp = q && q.generatedAt;
      if (calc && stamp) return String(calc) + ':' + String(stamp);
    } catch (e) {}
    return row && row.quoteKey ? String(row.quoteKey) : null;
  }

  function normaliseRows(obj) {
    try {
      var rows = obj && obj.quotes;
      if (Object.prototype.toString.call(rows) !== '[object Array]') return false;
      var changed = false;
      for (var i = 0; i < rows.length; i++) {
        var k = canonicalKey(rows[i]);
        if (k && rows[i] && rows[i].quoteKey !== k) { rows[i].quoteKey = k; changed = true; }
      }
      return changed;
    } catch (e) { return false; }
  }

  function wrapAccountFetch() {
    try {
      var orig = window.fetch;
      if (typeof orig !== 'function') return;
      window.fetch = function (input, init) {
        var url = '';
        try { url = String((input && input.url) || input || ''); } catch (e) {}
        if (!ACCOUNT_RE.test(url)) return orig.apply(this, arguments);

        /* Outbound: rewrite the keys this browser is about to store on the account, so a
           plan is never filed a second time under the other scheme. */
        var args = arguments;
        try {
          if (init && init.body && typeof init.body === 'string') {
            var body = JSON.parse(init.body);
            if (normaliseRows(body)) {
              var patched = {};
              for (var p in init) if (Object.prototype.hasOwnProperty.call(init, p)) patched[p] = init[p];
              patched.body = JSON.stringify(body);
              args = [input, patched];
            }
          }
        } catch (e) {}

        return orig.apply(this, args).then(function (res) {
          /* Inbound: present one scheme to both readers. The response is rebuilt rather
             than mutated, because a Response body can only be read once and the caller
             still has to be able to read it. */
          try {
            if (!res || !res.ok) return res;
            var ct = '';
            try { ct = String(res.headers.get('content-type') || ''); } catch (e) {}
            if (ct.indexOf('json') < 0) return res;
            return res.clone().json().then(function (data) {
              if (!normaliseRows(data)) return res;
              try { console.log(LOG, 'reconciled account plan keys'); } catch (e) {}
              return new Response(JSON.stringify(data), {
                status: res.status, statusText: res.statusText, headers: res.headers
              });
            })['catch'](function () { return res; });
          } catch (e) { return res; }
        });
      };
    } catch (e) {}
  }

  /* ---------------------------------------------------------------- deleting */

  function pathKey(calcType) {
    var t = String(calcType || '').toLowerCase();
    if (t.indexOf('flat') >= 0) return 'flattop_quotes';
    if (t.indexOf('blade') >= 0) return 'blade_quotes';
    if (t.indexOf('perf') >= 0 || t.indexOf('mesh') >= 0) return 'perfmesh_quotes';
    return 'ullc_quotes';
  }

  function repaint() {
    try {
      if (typeof window.renderQuotesPanel === 'function') { window.renderQuotesPanel(); return; }
    } catch (e) {}
    location.reload();
  }

  /* The pill carries calculator + quote number, so that is what identifies the target. But
     the copies of a duplicated plan carry DIFFERENT numbers (see fingerprint above), so
     matching on the number alone would delete one pill and leave eighty-nine behind. The
     target's fingerprint is taken first and everything sharing it goes too - the customer
     thinks of it as one plan, not ninety, and pressing x has to mean what it looks like. */
  function deleteQuote(calcType, quoteNum) {
    try {
      var key = pathKey(calcType);
      var arr = read(key);
      if (!arr) return;

      var targetFp = null;
      for (var t = 0; t < arr.length; t++) {
        var c = arr[t] || {};
        if (String(c.quoteNum) === String(quoteNum) &&
            (!calcType || String(c.calcType || '') === String(calcType))) {
          targetFp = fingerprint(c);
          break;
        }
      }

      var out = [], removed = 0;
      for (var i = 0; i < arr.length; i++) {
        var q = arr[i] || {};
        var sameNum = String(q.quoteNum) === String(quoteNum) &&
                      (!calcType || String(q.calcType || '') === String(calcType));
        var sameFp = targetFp !== null && fingerprint(q) === targetFp;
        if (sameNum || sameFp) { removed++; continue; }
        out.push(q);
      }
      if (!removed) return;
      rawSet(key, JSON.stringify(out));

      /* The pointer to the open quote must not be left dangling at a plan that no longer
         exists, or the page reopens an empty review. */
      try {
        var base = key.replace(/_quotes$/, '');
        var act = localStorage.getItem(base + '_active_quote');
        if (act != null && String(act) === String(quoteNum)) {
          localStorage.removeItem(base + '_active_quote');
        }
      } catch (e) {}

      try { console.log(LOG, 'deleted', removed, 'entries for', calcType, quoteNum); } catch (e) {}
      repaint();
    } catch (e) {}
  }

  /* ---------------------------------------------------------------- the controls */

  var CSS_ID = 'cf-dedupe-css';

  function css() {
    if (document.getElementById(CSS_ID)) return;
    var s = document.createElement('style');
    s.id = CSS_ID;
    s.textContent =
      '.qr-quote-pill{position:relative;}' +
      '.cf-qr-del{position:absolute;top:-6px;right:-6px;width:18px;height:18px;line-height:16px;' +
        'padding:0;border-radius:50%;border:1px solid #cbd5e1;background:#fff;color:#64748b;' +
        'font-size:12px;font-weight:700;cursor:pointer;display:none;text-align:center;}' +
      '.qr-quote-pill:hover .cf-qr-del,.cf-qr-del:focus{display:block;}' +
      '.cf-qr-del:hover{background:#dc2626;border-color:#dc2626;color:#fff;}' +
      '#cf-dedupe-btn{background:#fff;color:#b45309;border:1px solid #f59e0b;}' +
      '#cf-dedupe-btn:hover{background:#fffbeb;}';
    (document.head || document.documentElement).appendChild(s);
  }

  function addPillDeletes() {
    var pills = document.querySelectorAll('.qr-quote-pill');
    if (!pills.length) return;
    css();
    for (var i = 0; i < pills.length; i++) {
      var p = pills[i];
      if (p.querySelector('.cf-qr-del')) continue;
      var x = document.createElement('button');
      x.type = 'button';
      x.className = 'cf-qr-del';
      x.setAttribute('aria-label', 'Delete this plan');
      x.setAttribute('title', 'Delete this plan');
      x.textContent = '×';
      p.appendChild(x);
    }
  }

  function addDedupeButton() {
    var del = document.getElementById('qr-clear-btn');
    if (!del) return;
    var n = countDuplicates();
    var b = document.getElementById('cf-dedupe-btn');
    if (!n) { if (b && b.parentNode) b.parentNode.removeChild(b); return; }
    css();
    if (!b) {
      b = document.createElement('button');
      b.type = 'button';
      b.id = 'cf-dedupe-btn';
      b.className = 'qr-btn qr-btn--ghost';
      b.addEventListener('click', function () {
        var count = countDuplicates();
        if (!count) return;
        if (!window.confirm('Remove ' + count + ' duplicate cop' + (count === 1 ? 'y' : 'ies') +
              ' of plans you already have?\n\nOnly exact copies are removed. Every plan you ' +
              'designed is kept, and nothing is removed from our office records.')) return;
        sweep();
        repaint();
      });
      del.parentNode.insertBefore(b, del);
    }
    var want = 'Remove ' + n + ' duplicate' + (n === 1 ? '' : 's');
    if (b.textContent !== want) b.textContent = want;
  }

  /* Capture phase, so the x is handled before the pill's own click opens the quote. */
  function bindClicks() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var x = t.closest('.cf-qr-del');
      if (!x) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      var pill = x.closest('.qr-quote-pill');
      if (!pill || !pill.dataset) return;
      var name = String(pill.textContent || '').replace('×', '').trim() || 'this plan';
      if (!window.confirm('Delete "' + name + '"?\n\nIt is removed from this browser and from ' +
            'your saved plans. Our office copy is not affected.')) return;
      deleteQuote(pill.dataset.calcType, pill.dataset.quoteNum);
    }, true);
  }

  /* ---------------------------------------------------------------- boot */

  function tick() {
    try {
      addPillDeletes();
      addDedupeButton();
    } catch (e) {}
  }

  function start() {
    wrapSetItem();
    sweep();

    if (location.pathname.indexOf('/pages/quote-review') !== 0) return;

    bindClicks();
    tick();
    try {
      if (window.MutationObserver) {
        var t = null;
        new MutationObserver(function () {
          if (t) return;
          t = setTimeout(function () { t = null; tick(); }, 250);
        }).observe(document.body, { childList: true, subtree: true });
      }
    } catch (e) {}
    setInterval(tick, 1500);
    try { console.log(LOG, 'installed'); } catch (e) {}
  }

  /* cf-account.js captures localStorage.setItem at ITS load time and writes downloaded plans
     through that captured reference, so if it loaded first its writes go around the wrapper
     above. It is also asynchronous - the append happens when the account round trip returns,
     which is after the sweep on this file's own load. The fetch reconciliation should stop
     those downloads happening at all, but that is the fix and this is the seatbelt: sweep
     again when the sync reports in, and on a short ladder afterwards in case the event never
     fires. Each sweep is a no-op when there is nothing to collapse. */
  function reSweep() { try { if (sweep()) tick(); } catch (e) {} }

  try {
    document.addEventListener('cf:account-synced', reSweep);
    document.addEventListener('cf:account-ready', reSweep);
  } catch (e) {}
  setTimeout(reSweep, 1200);
  setTimeout(reSweep, 4000);
  setTimeout(reSweep, 9000);

  /* All three must be up before any other script reads the quote arrays or talks to the
     account proxy, so they do not wait for DOMContentLoaded. cf-account.js captures
     localStorage.setItem at load time, so this file has to be registered ahead of it. */
  wrapSetItem();
  wrapAccountFetch();
  try { sweep(); } catch (e) {}

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
